import type { EmbeddedAppBridgeAdapter } from "../types.js";
import type { MerchantRuntimeConfig } from "./config.js";

interface PlatformGlobal {
  idToken?: () => Promise<string>;
  config?: {
    apiKey?: string;
    host?: string;
  };
  navigate?: (url: string) => void;
}

declare global {
  interface Window {
    shopify?: PlatformGlobal;
  }
}

export function initializeEmbeddedAppBridge(
  config: MerchantRuntimeConfig,
): EmbeddedAppBridgeAdapter {
  void config;
  return createBrowserPlatformAdapter();
}

function createBrowserPlatformAdapter(): EmbeddedAppBridgeAdapter {
  return {
    async getSessionToken(_options) {
      const impersonationToken = readImpersonationToken();
      if (impersonationToken !== undefined) {
        return impersonationToken;
      }

      return readSessionTokenFromPlatformGlobal();
    },
    redirect(url) {
      redirectThroughPlatformWhenAvailable(url);
    },
  };
}

function readImpersonationToken(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  const token = new URLSearchParams(window.location.search).get("priceRescueImpersonationToken");
  if (token === null || token.length === 0) return undefined;
  assertImpersonationTokenIsCurrent(token);
  return token;
}

function assertImpersonationTokenIsCurrent(token: string): void {
  const expiresAtEpochSeconds = readJwtExpiration(token);
  if (expiresAtEpochSeconds === undefined) return;
  const nowEpochSeconds = Math.floor(Date.now() / 1_000);
  if (expiresAtEpochSeconds > nowEpochSeconds) return;

  throw new Error(
    "Merchant impersonation session expired. Launch merchant impersonation again from Admin UI.",
  );
}

function readJwtExpiration(token: string): number | undefined {
  const payload = token.split(".")[1];
  if (payload === undefined) return undefined;

  try {
    const decoded = JSON.parse(decodeBase64Url(payload)) as { exp?: unknown };
    return typeof decoded.exp === "number" ? decoded.exp : undefined;
  } catch {
    return undefined;
  }
}

function decodeBase64Url(value: string): string {
  const padded = value
    .replaceAll("-", "+")
    .replaceAll("_", "/")
    .padEnd(Math.ceil(value.length / 4) * 4, "=");
  return atob(padded);
}

async function readSessionTokenFromPlatformGlobal(): Promise<string> {
  if (typeof window !== "undefined" && window.shopify?.idToken !== undefined) {
    return window.shopify.idToken();
  }

  throw new Error("Platform App Bridge session token provider is unavailable.");
}

function redirectThroughPlatformWhenAvailable(url: string): void {
  if (typeof window === "undefined") return;

  if (window.shopify?.navigate !== undefined) {
    window.shopify.navigate(url);
    return;
  }

  window.location.assign(url);
}
